import React, { useCallback, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Checkbox,
  Combobox,
  DateErrorType,
  DateType,
  Box as FormActions,
  Box as FormFields,
  Input,
  Radio,
  SingleDate,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { useDateInput } from 'hooks/forms/useDateInput'
import { contactActionAtom } from './atoms/contactAtoms'
import { BankAccountRequirmentsCallout } from './components/FiatInstrument/BankAccountRequirmentsCallout'
import { useContactForm } from './hooks/useContactForm'
import { populateContactFormWithBeneficiaryDetails } from './utils/fiatInstruments'
import { ContactFormKeys } from './schemas/contactSchema'


export const ContactForm = () => {
  const { t } = useTranslation()

  const contactAction = useAtomValue(contactActionAtom)

  const { meta, onClose } = contactAction || {}
  const { updateRequiredForBankAccountDestination, contactBeneficiary, contactRequiredFields } =
    meta || {}

  const {
    singleDateLabels,
    hasDateErrors,
    getDateFields,
    setDateInputValue,
    setDateErrors,
    getDateErrors,
    getDateErrorText,
  } = useDateInput()

  const {
    formFields,
    isCountriesLoading,
    errors,
    control,
    isSubmitting,
    isPostingContact,
    isCompany,
    isIndividual,
    isEditingContact,
    isUpdatingContact,
    requestError,
    isUkraineSelected,
    isCryptoFlow,
    handleSubmit,
    onSubmit,
    setValue,
    onChangeCountry,
    trigger,
  } = useContactForm({
    setDateErrors,
    updateRequiredForBankAccountDestination,
    requiredFieldsForContactUpdate: contactRequiredFields?.requiredFields,
  })

  useEffect(() => {
    if (contactRequiredFields?.requiredFields) {
      trigger(contactRequiredFields?.requiredFields)
    }
  }, [trigger])

  useEffect(() => {
    if (contactBeneficiary) {
      populateContactFormWithBeneficiaryDetails(contactBeneficiary, setValue)
    }
  }, [contactBeneficiary])

  const isSubmittingAddContact = isSubmitting || isPostingContact
  const isSubmittingUpdateContact = isSubmitting || isUpdatingContact

  const getOptionalText = useCallback((field: ContactFormKeys) => {
    if (isCryptoFlow) {
      return (field === 'region' || field === 'addressLine2') ? t('optional') : undefined
    }

    return contactRequiredFields?.requiredFields?.includes(field)
  }, [isCryptoFlow, contactRequiredFields?.requiredFields])

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit(onSubmit)} className="w-full">
      {contactRequiredFields?.calloutMessage && (
        <BankAccountRequirmentsCallout calloutMessage={contactRequiredFields.calloutMessage} />
      )}
      <FormFields width="full" flexCol gapY={16} paddingT={24}>
        {requestError?.show && <RequestErrorCallout requestError={requestError} />}
        <Radio
          testId="entity-type"
          {...formFields.entityType}
          horizontal
          disabled={!!contactBeneficiary}
        />
        {isCompany && (
          <Input
            testId="company-name"
            {...formFields.companyName}
            onChange={e => {
              setValue('companyName', e?.target?.value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
        )}
        {isIndividual && (
          <Box flex gapX={16}>
            <Box className="w-full">
              <Input
                testId="first-name"
                {...formFields.firstName}
                onChange={e => {
                  setValue('firstName', e?.target?.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              />
            </Box>
            <Box className="w-full">
              <Input
                testId="last-name"
                {...formFields.lastName}
                onChange={e => {
                  setValue('lastName', e?.target?.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              />
            </Box>
          </Box>
        )}
        <Input
          testId="description"
          {...formFields.contactDescription}
          onChange={e => {
            setValue('contactDescription', e?.target?.value, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }}
        />
        {isCompany && <Checkbox {...formFields.isSelfOwned} />}
        <Box flexCol width="full">
         {!isCryptoFlow && <Box flexCol width="full" paddingB={16} >
            <h2 className="text-foreground-prominent text-h2">
              {t('contacts.requiredDetailsForPayments')}
            </h2>
          </Box>}
          <Box width="full" flexCol gapY={16}>
            {isCompany && (
              <>
                <Input
                  testId="registration-number"
                  {...formFields.companyRegistrationNumber}
                  onChange={e => {
                    setValue('companyRegistrationNumber', e?.target?.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
              optionalText={getOptionalText('companyRegistrationNumber')}
                />
                <Combobox
                  testid="registration-country"
                  {...formFields.companyRegistrationCountry}
                  onChange={value => onChangeCountry('companyRegistrationCountry', value)}
                  loading={isCountriesLoading}
                  optionalText={getOptionalText('companyRegistrationCountry')}
                />
              </>
            )}
            {isIndividual && (
              <Combobox
                testid="country-of-residence"
                {...formFields.countryOfResidence}
                onChange={value => onChangeCountry('countryOfResidence', value)}
                loading={isCountriesLoading}
              optionalText={getOptionalText('countryOfResidence')}
              />
            )}
            <Input
              testId="address-line-1"
              {...formFields.addressLine1}
              onChange={e => {
                setValue('addressLine1', e?.target?.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            optionalText={getOptionalText('addressLine1')}
            />
            <Input
              testId="address-line-2"
              {...formFields.addressLine2}
              onChange={e => {
                setValue('addressLine2', e?.target?.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
            optionalText={getOptionalText('addressLine2')}
            />
            <Box flex className="w-full" gapX={16}>
              <Box className="w-full">
                <Input
                  testId="city"
                  {...formFields.city}
                  onChange={e => {
                    setValue('city', e?.target?.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                optionalText={getOptionalText('city')}
                />
              </Box>
              <Box className="w-full">
                <Input
                  testId="post-code"
                  {...formFields.postCode}
                  onChange={e => {
                    setValue('postCode', e?.target?.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }}
                optionalText={getOptionalText('postCode')}
                />
              </Box>
            </Box>
            {isUkraineSelected ? (
              <Combobox
                testId="regionCode"
                {...formFields.regionCode}
                label={t('formFields.region')}
                onChange={value =>
                  setValue('regionCode', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
              />
            ) : (
              <Input
                testId="region"
                {...formFields.region}
                label={t('formFields.region')}
              optionalText={getOptionalText('region')}
                onChange={e => {
                  setValue('region', e?.target?.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              />
            )}
            {isIndividual && (
              <Box>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <SingleDate
                      date={getDateFields(value)}
                      labels={{
                        ...singleDateLabels,
                        errorMessage: getDateErrorText(error),
                      }}
                      errors={getDateErrors(error)}
                      onChange={(date: DateType) => onChange(setDateInputValue(date))}
                      onValidate={(errors: DateErrorType) => setDateErrors(errors)}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Box>

        <FormActions flex justify="between" className="pb-10">
          <Button testId="cancel" variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>

          <Button
            testId={isEditingContact ? 'update-contact' : 'create-contact'}
            type="submit"
            variant="primary"
            loading={isEditingContact ? isSubmittingUpdateContact : isSubmittingAddContact}
            disabled={!!Object.keys(errors)?.length || hasDateErrors}
          >
            {isEditingContact ? t('contacts.updateContact') : t('contacts.createContact')}
          </Button>
        </FormActions>
      </FormFields>
    </form>
  )
}
